// Wing Spotlight Tours — premium discovery moments.
//
// Philosophy: short, emotional, restrained. Never explain — invite curiosity.
// Each step targets a `data-spotlight-id` somewhere in the DOM. If the target
// isn't mounted when the tour fires, that step is skipped silently.
//
// Bilingual copy (en / nl). The runtime picks the language from i18next.
//
// Each tour also exposes:
//   - `meta.route` — page to navigate to before triggering (used by the
//                    Settings → Tours hub).
//   - `meta.title` / `meta.subtitle` — hub list item labels.

export const TOUR_IDS = {
    FIRST_LOGIN:  "wing_tour_first_v1",
    MOMENTUM:     "wing_tour_momentum_v1",
    LOW_RECOVERY: "wing_tour_low_recovery_v1",
    FIRST_SPIN:   "wing_tour_first_spin_v1",
    AI_COACH:     "wing_tour_ai_coach_v1",
    NUTRITION:    "wing_tour_nutrition_v1",
    CHALLENGES:   "wing_tour_challenges_v1",
    TILE_REORDER: "wing_tour_tile_reorder_v1",
};

const step = (target, en, nl, opts = {}) => ({
    target,
    en: { title: en[0], subtitle: en[1] },
    nl: { title: nl[0], subtitle: nl[1] },
    placement: opts.placement || "auto",
    radius: opts.radius || 24,
    padding: opts.padding ?? 12,
});

// Hub metadata — what the Settings → Tours page renders for each entry.
// Every item shown to the user must have a meta entry here.
export const TOUR_META = {
    [TOUR_IDS.FIRST_LOGIN]: {
        route: "/dashboard",
        en: { title: "Full app tour",         subtitle: "The 4-step welcome tour" },
        nl: { title: "Volledige rondleiding", subtitle: "De 4-staps welkomsttour" },
    },
    [TOUR_IDS.MOMENTUM]: {
        route: "/dashboard",
        en: { title: "What is Momentum?",      subtitle: "How your rhythm builds over 7 days" },
        nl: { title: "Wat is Momentum?",       subtitle: "Hoe je ritme zich opbouwt over 7 dagen" },
    },
    [TOUR_IDS.AI_COACH]: {
        route: "/coach",
        en: { title: "Your AI Coach",          subtitle: "Daily insights and chat" },
        nl: { title: "Jouw AI Coach",          subtitle: "Dagelijkse inzichten en chat" },
    },
    [TOUR_IDS.FIRST_SPIN]: {
        route: "/dashboard",
        en: { title: "Wheel of Rewards",       subtitle: "How spins and tiers work" },
        nl: { title: "Wheel of Rewards",       subtitle: "Hoe draaibeurten en tiers werken" },
    },
    [TOUR_IDS.NUTRITION]: {
        route: "/nutrition",
        en: { title: "Nutrition",              subtitle: "Logging meals and macros" },
        nl: { title: "Voeding",                subtitle: "Maaltijden en macro's loggen" },
    },
    [TOUR_IDS.CHALLENGES]: {
        route: "/challenges",
        en: { title: "Challenges",             subtitle: "1v1 and group competitions" },
        nl: { title: "Challenges",             subtitle: "1v1 en groepscompetities" },
    },
    [TOUR_IDS.TILE_REORDER]: {
        route: "/dashboard",
        en: { title: "Reorder your tiles",     subtitle: "Personalise your dashboard" },
        nl: { title: "Tegels herordenen",      subtitle: "Maak je dashboard persoonlijk" },
    },
    [TOUR_IDS.LOW_RECOVERY]: {
        route: "/dashboard",
        en: { title: "Recovery & Momentum",    subtitle: "Why low recovery slows you" },
        nl: { title: "Recovery & Momentum",    subtitle: "Waarom lage recovery vertraagt" },
    },
};

export const TOURS = {
    // ─── First-login: 4 spotlights ────────────────────────────────────
    [TOUR_IDS.FIRST_LOGIN]: [
        step(
            "momentum-hero",
            ["Momentum",       "Shows how strongly your healthy rhythm is building over time. Consistency matters more than one perfect day."],
            ["Momentum",       "Laat zien hoe sterk je gezonde ritme zich opbouwt. Consistentie weegt zwaarder dan één perfecte dag."],
            { padding: 14, radius: 28 },
        ),
        step(
            "ai-coach",
            ["Your AI Coach",  "Learns from your habits and guides your next best action. Small steps compound into real progress."],
            ["Jouw AI Coach",  "Leert van je gewoontes en wijst je telkens de volgende beste stap. Kleine stappen worden grote resultaten."],
        ),
        step(
            "wheel-handle",
            ["Wheel of Rewards", "Stay active and build momentum to unlock more spins. Consistency increases your opportunities."],
            ["Wheel of Rewards", "Blijf actief en bouw momentum op om meer draaibeurten te ontgrendelen. Hoe consistenter, hoe meer kansen."],
            { radius: 32, padding: 16 },
        ),
    ],

    // ─── Single-spotlight feature tours ───────────────────────────────
    [TOUR_IDS.MOMENTUM]: [
        step("momentum-hero",
            ["Momentum reacts gradually", "It's a 7-day pulse — give it a few days to settle in. Trend matters more than today's number."],
            ["Momentum past zich geleidelijk aan", "Het is een 7-daagse puls — geef hem een paar dagen. De trend telt zwaarder dan vandaag."],
            { padding: 14, radius: 28 }),
    ],
    [TOUR_IDS.AI_COACH]: [
        step("coach-tabs",
            ["Daily insights, on tap",  "Your coach learns your patterns and answers questions about sleep, nutrition, recovery and training."],
            ["Dagelijkse inzichten",    "Je coach leert je patronen kennen en beantwoordt vragen over slaap, voeding, recovery en training."]),
    ],
    [TOUR_IDS.FIRST_SPIN]: [
        step("wheel-handle",
            ["Higher momentum, better odds",
             "Swipe left on the rings card to reveal the wheel. Your tier influences which rewards you can land."],
            ["Hoger momentum, betere kansen",
             "Swipe links op de rings-kaart om het wiel te onthullen. Je tier bepaalt welke beloningen mogelijk zijn."],
            { radius: 32, padding: 16 }),
    ],
    [TOUR_IDS.LOW_RECOVERY]: [
        step("tile-recovery",
            ["Recovery shapes momentum", "Lower recovery slows the build — sleep and HRV are your levers."],
            ["Recovery vormt je momentum", "Minder recovery vertraagt de groei — slaap en HRV zijn je hefbomen."]),
    ],
    [TOUR_IDS.TILE_REORDER]: [
        step("dashboard-edit-pencil",
            ["Make it yours",   "Tap the pencil to enter edit mode, then tap two tiles to swap them. The layout saves automatically."],
            ["Maak het van jou", "Tik op het potlood om te bewerken, tik dan twee tegels om ze te wisselen. Layout wordt automatisch opgeslagen."],
            { radius: 999, padding: 6 }),
    ],

    // ─── Multi-step page tours ────────────────────────────────────────
    [TOUR_IDS.NUTRITION]: [
        step("nutrition-summary",
            ["Daily nutrition at a glance", "Calories and macros in one card. Tap to set or adjust your goals."],
            ["Voeding in één oogopslag",    "Calorieën en macro's in één kaart. Tik om je doelen aan te passen."],
            { padding: 14, radius: 28 }),
        step("nutrition-add-fab",
            ["Log a meal in seconds",       "Search any food, scan a barcode or use AI quick-add. WYNG tracks the macros for you."],
            ["Maaltijd in seconden loggen", "Zoek voeding, scan een barcode of gebruik AI quick-add. WYNG telt de macro's voor je."],
            { radius: 999, padding: 10 }),
    ],
    [TOUR_IDS.CHALLENGES]: [
        step("challenges-create",
            ["Compete with friends",   "Set up 1v1 duels or group challenges across any metric — steps, calories, sleep, you choose."],
            ["Wedstrijd met vrienden", "Start 1v1 duels of groepsuitdagingen op elke metric — stappen, calorieën, slaap, jij kiest."],
            { padding: 12, radius: 24 }),
    ],
};
